import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ReactiveFormsModule, FormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ButtonComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { NotificationService } from 'src/app/shared/services/notification.service';

@Component({
  selector: 'ppl-invoice-adjust-void-entry',
  standalone: true,
  imports: [
    CommonModule,
    NgZorroModule,
    ButtonComponent,
    ReactiveFormsModule,
    NzDatePickerModule,
    FormsModule,
  ],
  templateUrl: './invoice-adjust-void-entry.component.html',
  styleUrl: './invoice-adjust-void-entry.component.css'
})
export class InvoiceAdjustVoidEntryComponent implements OnInit, OnDestroy {
 
  private destroy$ = new Subject<void>();
  private serviceCodesSub?: Subscription;
 
  @Input() timesheetData?: any;
  @Input() searchParams: any;
  @Input() actionData: any;
  @Output() timesheetAdjustedOrVoided = new EventEmitter();
 
  adjustVoidTimesheetForm!: FormGroup;
  reasonList: LookupValueItem[] = [];
  serviceCodes: LookupValueItem[] = [];
  serviceDescriptions: any[] = [];
  serviceCodesData: any[] = [];
  // Read-only display fields driven by the selected Service Description (authList entry).
  selectedPplAuthNum = '';
  selectedRegionalCenter = '';
  buttonLabel = '';
  actionType = '';
  programName!: string;
  isAuthFail = false;
  insufficientAuthMessage = 'Error: No Authorization for pay period and participant.';
  compareServiceCode = (o1: any, o2: any): boolean => o1?.lkpValueCode === o2?.lkpValueCode;

  readonly source = 'invoice';
 
  constructor(
    private fb: FormBuilder,
    private dateTimeFormatterService: DateTimeFormatterService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService,
    private notificationService: NotificationService
  ) { }
 
  ngOnInit(): void {
 
    console.log(this.timesheetData, 'timesheetDatatimesheetData');
 
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
 
    this.actionType = this.actionData.buttonAction;
    this.buttonLabel = this.actionData.button_label;
 
    // Reason lookup — sort alphabetically with any "Other" variant pinned to the bottom
    this.reasonList = this.sortReasonsOtherLast(this.lookupService.getLookupValue('Reason_Adjustment'));

    // Service codes lookup (for adjust only)
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');
 
    // Build form based on action type
    // adjustTimesheet → serviceDate + serviceCode + reason
    // voidTimesheet   → reason only
    this.adjustVoidTimesheetForm = this.fb.group({
      serviceDate: [
        this.actionType === 'adjustTimesheet' || this.actionType === 'adjustAllTimesheet' ? (this.timesheetData?.serviceDate ?? null) : null
      ],
      serviceCode: [
        this.actionType === 'adjustTimesheet' || this.actionType === 'adjustAllTimesheet' ? (this.timesheetData?.serviceCode ?? null) : null
      ],
      serviceDescription: [null],
      billedUnits: [
        this.actionType === 'adjustTimesheet' ? (this.timesheetData?.billedUnits ?? null) : null
      ],
      unitPrice: [
        this.actionType === 'adjustTimesheet' ? (this.timesheetData?.unitPrice ?? null) : null
      ],
      reason: [null, [Validators.required]],
      reasonDetails: [null, [Validators.maxLength(160)]],
      crmNumber: [null, [Validators.required, Validators.maxLength(50)]],
    });

    this.adjustVoidTimesheetForm.get('reason')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((reason: any) => {
        const reasonDetailsControl = this.adjustVoidTimesheetForm.get('reasonDetails');
        // Match "Other" and "Other - Explain" variants
        const isOtherReason = !!reason?.lkpValueName?.toLowerCase()?.startsWith('other');
        if (isOtherReason) {
          reasonDetailsControl?.setValidators([Validators.required, Validators.maxLength(160)]);
        } else {
          reasonDetailsControl?.setValidators([Validators.maxLength(160)]);
        }
        reasonDetailsControl?.updateValueAndValidity();
    });

    // Set required validators for adjust fields
    if (this.actionType === 'adjustTimesheet') {
      this.adjustVoidTimesheetForm.get('serviceDate')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('serviceCode')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('serviceDescription')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('serviceDate')?.updateValueAndValidity();
      this.adjustVoidTimesheetForm.get('serviceCode')?.updateValueAndValidity();
      this.adjustVoidTimesheetForm.get('serviceDescription')?.updateValueAndValidity();
      this.adjustVoidTimesheetForm.get('unitPrice')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('billedUnits')?.setValidators([Validators.required]);

      this.loadServiceCodesByConsumer(this.timesheetData?.serviceDate, true);

      // A new service date can change which auths apply, so the user must pick a new code/auth.
      this.adjustVoidTimesheetForm.get('serviceDate')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((date: Date | null) => {
          this.adjustVoidTimesheetForm.patchValue({ serviceCode: null, serviceDescription: null });
          this.serviceDescriptions = [];
          this.serviceCodesData = [];
          this.setAuthDisplayFields(null);
          if (date) {
            this.loadServiceCodesByConsumer(this.dateTimeFormatterService.formatDateToString(new Date(date)), false);
          }
        });
    }
    if (this.actionType === 'adjustAllTimesheet') {
      this.adjustVoidTimesheetForm.get('serviceDate')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('serviceCode')?.setValidators([Validators.required]);
      this.adjustVoidTimesheetForm.get('serviceCode')?.updateValueAndValidity();
      this.adjustVoidTimesheetForm.get('serviceCode')?.updateValueAndValidity();
    }
  }

  /** prefillFromTimesheet: pre-select the timesheet's existing service code/auth (initial load only). */
  private loadServiceCodesByConsumer(serviceDate: string, prefillFromTimesheet: boolean): void {
    this.isAuthFail = false;
    // Re-filter from the full lookup each time, since a previous date may have narrowed the list.
    const allServiceCodes: LookupValueItem[] = this.lookupService.getLookupValue('timesheet_filter_serviceCode') ?? [];
    const payload = {
      consumerId: this.timesheetData?.participantId,
      periodFromDate: serviceDate,
      periodToDate: serviceDate,
      programCode: this.programName,
      providerId: this.timesheetData?.providerId,
      source: 'invoice'
    };

    // Cancel any in-flight request so a slower response for an older date can't win.
    this.serviceCodesSub?.unsubscribe();
    this.serviceCodesSub = this.timesheetService
      .getServiceCodesByConsumer(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if ((data.responsedata == 'Auth Fail' && data.status == 404) ||
          !data.responsedata || (Array.isArray(data.responsedata) && data.responsedata.length === 0)) {
          this.isAuthFail = true;
          this.serviceCodes = [];
          return;
        }

        this.serviceCodesData = data.responsedata;
        this.serviceCodes = allServiceCodes.filter(sc =>
          data.responsedata.some((item: any) => item.serviceCodeDetailId.toString() === sc.lkpValueCode)
        );

        if (!prefillFromTimesheet) return;

        const matchedServiceCode = data.responsedata.find((item: any) =>
          item.serviceCodeDetailId.toString() === this.timesheetData?.serviceCode?.lkpValueCode
        );

        if (matchedServiceCode) {
          matchedServiceCode.authList = matchedServiceCode.authList ?? [];

          const existingAuthId = this.timesheetData?.tasAuthorizationDetailId;
          const existingAuthDesc = this.timesheetData?.authDesc;
          const hasMatch = matchedServiceCode.authList.some((d: any) =>
            d.ids === existingAuthId && d.desc === existingAuthDesc
          );
          if (!hasMatch && existingAuthId !== undefined && existingAuthDesc) {
            matchedServiceCode.authList.push({ ids: existingAuthId, desc: existingAuthDesc });
          }
        }

        this.serviceDescriptions = this.sortAuthListByDesc(matchedServiceCode?.authList ?? []);

        const matchedDescription = this.serviceDescriptions.find((d: any) =>
          d.ids === this.timesheetData?.tasAuthorizationDetailId
          && d.desc === this.timesheetData?.authDesc
        );
        if (matchedDescription) {
          this.adjustVoidTimesheetForm.patchValue({ serviceDescription: matchedDescription.ids });
          this.setAuthDisplayFields(matchedDescription);
        }
      });
  }

  /** Sorts reasons A→Z and pins any label starting with "Other" to the bottom of the list. */
  private sortReasonsOtherLast(reasons: LookupValueItem[] | null | undefined): LookupValueItem[] {
    if (!Array.isArray(reasons)) return [];
    const isOther = (r: LookupValueItem) => !!r?.lkpValueName?.toLowerCase()?.startsWith('other');
    const others = reasons.filter(isOther);
    const rest = reasons
      .filter(r => !isOther(r))
      .sort((a, b) => (a?.lkpValueName ?? '').localeCompare(b?.lkpValueName ?? ''));
    return [...rest, ...others];
  }

  onServiceCodeChange(serviceCode: LookupValueItem): void {
    const matched = this.serviceCodesData.find((item: any) =>
      item.serviceCodeDetailId.toString() === serviceCode?.lkpValueCode
    );
    this.serviceDescriptions = this.sortAuthListByDesc(matched?.authList ?? []);
    this.adjustVoidTimesheetForm.patchValue({ serviceDescription: null });
    this.setAuthDisplayFields(null);
  }

  /** Updates the read-only PPL Authorization Number / Regional Center display for the selected Service Description. */
  onServiceDescriptionChange(ids: any): void {
    const matched = this.serviceDescriptions.find((d: any) => d.ids === ids);
    this.setAuthDisplayFields(matched);
  }

  private setAuthDisplayFields(authListItem: any): void {
    this.selectedPplAuthNum = authListItem?.pplAuthNum ?? '';
    this.selectedRegionalCenter = authListItem?.orgName ?? '';
  }

  private sortAuthListByDesc(authList: any[]): any[] {
    return [...authList].sort((a, b) =>
      (a?.desc ?? '').localeCompare(b?.desc ?? '')
    );
  }
 
  // ─── Format Helpers ─────────────────────────────────────────────────────────

  // Convert Date → 'YYYY-MM-DD' as required by the API
  private formatDateToApiFormat(date: any): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ─── Save ───────────────────────────────────────────────────────────────────
 
  onSave(): void {
    if (this.adjustVoidTimesheetForm?.valid) {
      let placeholder = '';
      let urlEndpoint = '';
      let payload: any = {};
      const reasonCode = this.adjustVoidTimesheetForm?.get('reason')?.value?.lkpValueCode;
      const reasonDetails = this.adjustVoidTimesheetForm?.get('reasonDetails')?.value?.trim();
      switch (this.actionType) {
 
        case 'adjustTimesheet': {
          const payload = {
            invoiceLineId: this.timesheetData?.invoiceLineId,
            serviceDate: this.formatDateToApiFormat(this.adjustVoidTimesheetForm.get('serviceDate')?.value),
            serviceCode: Number(this.adjustVoidTimesheetForm.get('serviceCode')?.value?.lkpValueCode),
            tasAuthorizationDetailId: this.adjustVoidTimesheetForm.get('serviceDescription')?.value,
            unitPrice: this.adjustVoidTimesheetForm.get('unitPrice')?.value,
            billedUnits: this.adjustVoidTimesheetForm.get('billedUnits')?.value,
            reason: reasonDetails ? `${reasonCode} - ${reasonDetails}` : reasonCode,
            crmNumber: this.adjustVoidTimesheetForm.get('crmNumber')?.value?.trim()
          };

          this.timesheetService.adjustInvoice(payload, this.programName)
            .subscribe(() => this.timesheetAdjustedOrVoided.emit({}));
          break;
        }

        case 'voidTimesheet': {
          const payload = {
            invoiceLineId: this.timesheetData?.invoiceLineId,
            reason: reasonDetails ? `${reasonCode} - ${reasonDetails}` : reasonCode,
            crmNumber: this.adjustVoidTimesheetForm.get('crmNumber')?.value?.trim()
          };

          this.timesheetService.voidInvoice(payload, this.programName)
            .subscribe(() => this.timesheetAdjustedOrVoided.emit({}));
          break;
        }
        case 'adjustAllTimesheet': {
          const payload = {
            invoiceLineIds: this.timesheetData?.invoiceLineId,
            serviceDate: this.formatDateToApiFormat(this.adjustVoidTimesheetForm.get('serviceDate')?.value),
            serviceCode: Number(this.adjustVoidTimesheetForm.get('serviceCode')?.value?.lkpValueCode),
            reason: reasonDetails ? `${reasonCode} - ${reasonDetails}` : reasonCode,
            crmNumber: this.adjustVoidTimesheetForm.get('crmNumber')?.value?.trim()
          };

          this.timesheetService.invoiceAdjustALL(payload, this.programName)
            .subscribe({
              next: (res: any) => this.handleBulkResult(res, 'Adjust'),
              error: () => this.notificationService.error('Failed', 'Failed to Adjust')
            });
          break;
        }
        case 'voidAllTimesheets': {
          const payload = {
            invoiceLineIds: this.timesheetData?.invoiceLineId,
            reason: reasonDetails ? `${reasonCode} - ${reasonDetails}` : reasonCode,
            crmNumber: this.adjustVoidTimesheetForm.get('crmNumber')?.value?.trim()
          };

          this.timesheetService.invoiceVoidALL(payload, this.programName)
            .subscribe({
              next: (res: any) => this.handleBulkResult(res, 'Void'),
              error: () => this.notificationService.error('Failed', 'Failed to Void')
            });
          break;
        }
      }
 
    } else {
      this.adjustVoidTimesheetForm.markAllAsTouched();
    }
  }
 
  /** Bulk Adjust/Void: backend returns HTTP 200 with `responsedata: 'success' | 'failure'`. */
  private handleBulkResult(res: any, label: 'Adjust' | 'Void'): void {
    if ((res?.responsedata ?? '').toString().toLowerCase() === 'failure') {
      this.notificationService.error('Failed', `Failed to ${label}`);
      return;
    }
    this.timesheetAdjustedOrVoided.emit({});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
 